/**
 * Course ID Alias System
 * Maps reference IDs to database IDs for cases where they differ
 * 
 * This provides universal course ID resolution across the entire app
 */

// Bidirectional mapping: reference ID → database ID
export const COURSE_ID_ALIASES: Record<string, string> = {
  // React courses - map common references to DB IDs
  'react-fundamentals': 'basic-react',
  'react-intermediate': 'intermediate-react',
  'react-advanced': 'advanced-react',
  
  // JavaScript courses
  'javascript-intermediate': 'js-intermediate',
  'javascript-advanced': 'js-advanced',
  
  // Node.js
  'node-js': 'nodejs-fundamentals',
  
  // Machine Learning courses
  'machine-learning-intro': 'ml-fundamentals',
  'machine-learning': 'machine-learning-intro',
  'machine-learning-production': 'ml-advanced',
  'deep-learning': 'ml-advanced',
  
  // Missing courses - map to closest alternatives
  'quantum-machine-learning': 'reinforcement-learning-fundamentals',
  'multi-agent-rl': 'reinforcement-learning-fundamentals',
};

// Reverse mapping: database ID → canonical reference ID
export const DB_TO_REFERENCE_ALIASES: Record<string, string> = {
  'basic-react': 'react-fundamentals',
  'intermediate-react': 'react-intermediate',
  'advanced-react': 'react-advanced',
  'js-intermediate': 'javascript-intermediate',
  'js-advanced': 'javascript-advanced',
  'nodejs-fundamentals': 'node-js',
  'ml-fundamentals': 'machine-learning-intro',
  'machine-learning-intro': 'machine-learning',
  'ml-advanced': 'machine-learning-production',
};

/**
 * Resolves a course ID to its database ID (if an alias exists)
 */
export const resolveCourseId = (id: string): string => {
  return COURSE_ID_ALIASES[id] || id;
};

/**
 * Gets all possible IDs for a course (including the original ID and all aliases)
 * Useful for searching across both DB and static representations
 */
export const getAllAliasesForCourse = (id: string): string[] => {
  const aliases = new Set<string>([id]);
  
  // Add forward alias if exists
  if (COURSE_ID_ALIASES[id]) {
    aliases.add(COURSE_ID_ALIASES[id]);
  }
  
  // Add reverse alias if exists
  if (DB_TO_REFERENCE_ALIASES[id]) {
    aliases.add(DB_TO_REFERENCE_ALIASES[id]);
  }
  
  // Check if any alias maps to this ID
  for (const [refId, dbId] of Object.entries(COURSE_ID_ALIASES)) {
    if (dbId === id) aliases.add(refId);
    if (refId === id) aliases.add(dbId);
  }
  
  return [...aliases];
};

/**
 * Checks if two course IDs refer to the same course (considering aliases)
 */
export const isSameCourse = (id1: string, id2: string): boolean => {
  if (id1 === id2) return true;
  const aliases1 = getAllAliasesForCourse(id1);
  return aliases1.includes(id2);
};

/**
 * Resolves a course ID to its canonical database form
 * Use this before any database queries
 */
export const toDbCourseId = (referenceId: string): string => {
  return COURSE_ID_ALIASES[referenceId] || referenceId;
};

/**
 * Resolves a database course ID to its reference form
 * Use this for display or static data matching
 */
export const toReferenceCourseId = (dbId: string): string => {
  return DB_TO_REFERENCE_ALIASES[dbId] || dbId;
};
